import { computed, ref, watch, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ExpertQuoteFeeEstimateRequest } from "@shopexperts/legacy-api";
import { buildFeeBreakdownRows } from "@/components/expert/wallet/useWalletTransaction";

interface UseExpertQuoteFeeEstimateOptions {
  actingOrgId: MaybeRef<number>;
  amountCents: MaybeRef<number | null>;
  currency: MaybeRef<string>;
  enabled?: MaybeRef<boolean>;
  debounceMs?: number;
}

function isValidCurrency(value: string): value is string {
  return /^[A-Z]{3}$/.test(value);
}

function requestsMatch(
  a: ExpertQuoteFeeEstimateRequest | null,
  b: ExpertQuoteFeeEstimateRequest | null,
): boolean {
  if (!a || !b) return false;
  return (
    a.acting_org_id === b.acting_org_id &&
    a.amount_cents === b.amount_cents &&
    a.currency === b.currency
  );
}

export function useExpertQuoteFeeEstimate(options: UseExpertQuoteFeeEstimateOptions) {
  const api = useApi();
  const debounceMs = options.debounceMs ?? 400;
  const debouncedRequest = ref<ExpertQuoteFeeEstimateRequest | null>(null);
  const isDebouncing = ref(false);
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const requestSnapshot = computed((): ExpertQuoteFeeEstimateRequest | null => {
    const actingOrgId = unref(options.actingOrgId);
    const amountCents = unref(options.amountCents);
    const currency = unref(options.currency).trim().toUpperCase();
    const enabled = unref(options.enabled) ?? true;

    const canEstimate =
      enabled &&
      actingOrgId > 0 &&
      amountCents !== null &&
      amountCents > 0 &&
      isValidCurrency(currency);

    if (!canEstimate) return null;

    return {
      acting_org_id: actingOrgId,
      amount_cents: amountCents,
      currency,
    };
  });

  watch(
    requestSnapshot,
    (snapshot) => {
      if (debounceTimer) clearTimeout(debounceTimer);

      if (!snapshot) {
        debouncedRequest.value = null;
        isDebouncing.value = false;
        return;
      }

      if (requestsMatch(snapshot, debouncedRequest.value)) {
        isDebouncing.value = false;
        return;
      }

      isDebouncing.value = true;
      debounceTimer = setTimeout(() => {
        debouncedRequest.value = snapshot;
        isDebouncing.value = false;
      }, debounceMs);
    },
    { immediate: true },
  );

  const query = useQuery({
    queryKey: computed(() => ["expert", "wallet", "estimateQuoteFees", debouncedRequest.value]),
    queryFn: async () => {
      if (!debouncedRequest.value) {
        throw new Error("Missing quote fee estimate request");
      }

      return await api.expert.wallet.estimateQuoteFees({ body: debouncedRequest.value });
    },
    enabled: computed(() => debouncedRequest.value !== null),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });

  const requestMatchesDebounced = computed(() =>
    requestsMatch(requestSnapshot.value, debouncedRequest.value),
  );

  const isPending = computed(() => {
    if (!requestSnapshot.value) return false;
    if (isDebouncing.value || !requestMatchesDebounced.value) return true;
    if (!debouncedRequest.value) return true;

    return query.isFetching.value || query.isPending.value;
  });

  const estimate = computed(() => {
    if (!requestMatchesDebounced.value || isDebouncing.value) return null;
    return query.data.value ?? null;
  });

  const breakdown = computed(() => {
    if (!estimate.value) {
      return { breakdownRows: [], providerFeeLines: [] };
    }

    return buildFeeBreakdownRows(
      {
        gross_amount_cents: estimate.value.gross_amount_cents,
        provider_fee_cents: estimate.value.provider_fee_cents,
        provider_fee_details: estimate.value.provider_fee_details,
        commission_cents: estimate.value.commission_cents,
        commission_rate_bps: estimate.value.expert_level?.commission_rate_bps ?? null,
        net_amount_cents: estimate.value.net_amount_cents,
        currency: estimate.value.currency,
      },
      { perspective: "self" },
    );
  });

  return {
    estimate,
    breakdownRows: computed(() => breakdown.value.breakdownRows),
    providerFeeLines: computed(() => breakdown.value.providerFeeLines),
    isPending,
    isDebouncing: computed(() => isDebouncing.value),
    isFetching: computed(() => query.isFetching.value),
    hasBreakdown: computed(() => breakdown.value.breakdownRows.length > 0),
    query,
  };
}
